import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import { getData } from '@/features/shared/lib/api';
import { buildListParams } from '@/features/shared/lib/utils';
import IntroTab from '@/features/mypage/IntroTab';
import ScholarMetricsTab from '@/features/mypage/ScholarMetricsTab';
import CoResearchersTab from '@/features/mypage/CoResearchersTab';
import PublicationsTab from '@/features/mypage/PublicationsTab';
import DocCiteTrendsMini from '@/features/researcher/components/charts/DocCiteTrendsMini';

export default function MypageMyProfileView({ onEditClick, record, loading, error }) {
  const meta = record?.meta || {};
  const raa = record?.research_all_achievement || {};
  const scholarlyOutput = raa.resource_count ?? 0;
  const citationCount = raa.cited_count ?? 0;
  const citationCountFormatted = typeof citationCount === 'number' ? citationCount.toLocaleString() : String(citationCount ?? 0);
  const pubYearData = record?.publications_by_year?.data || [];
  const hIndexWos = meta.hindex_wos ?? 0;
  const hIndexScopus = meta.hindex_scopus ?? 0;
  const hIndexKci = meta.hindex_kci ?? 0;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleEditClick = () => {
    navigate('/mypage/edit/profile');
  };

  // URL 파라미터에서 tab 값 읽기
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['intro', 'metrics', 'co', 'pub'];
    return validTabs.includes(tabParam) ? tabParam : 'intro';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab()); // intro | metrics | co | pub

  // 논문 확인 현황 메타 (전체/확인/미확인)
  const [pubMeta, setPubMeta] = useState({ total: 0, confirmed: 0, unconfirmed: 0 });

  // URL 파라미터 변경 시 탭 업데이트
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['intro', 'metrics', 'co', 'pub'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // 논문 확인 현황 메타 조회 (perPage 1로 meta만 가져오기)
  useEffect(() => {
    const userId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;
    if (!userId) return;

    let mounted = true;
    (async () => {
      try {
        const params = buildListParams({
          page: 1,
          perPage: 1,
          extra: { ks: '', sub_ks: '', order_by: 'date', order: 'desc', my_library: true },
        });
        const res = await getData('/rm/v1/my_library/resources', params);
        if (!mounted) return;
        const result = res || {};
        const meta = result.meta || {};
        setPubMeta({
          total: meta.total ?? result.pageInfo?.totalResults ?? result.pageInfo?.total_results ?? 0,
          confirmed: meta.confirmed ?? 0,
          unconfirmed: meta.unconfirmed ?? 0,
        });
      } catch (e) {
        if (!mounted) return;
        console.error('pub meta fetch:', e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // 이름 포맷팅
  const fullName = useMemo(() => {
    if (!record) return '';
    const kor = record.scholar_name_kor || record.scholar_name;
    const eng = record.scholar_name_eng || record.scholar_name;
    return [eng, kor].filter(Boolean).join(' / ');
  }, [record]);

  // 부서/직급 정보
  const deptName = record?.dept_name || '';
  const colName = record?.col_name || '';
  const position = record?.sinbun_name || '';

  // 연구 분야
  const researchArea = useMemo(() => {
    if (!record) return '';
    if (Array.isArray(record.research_field)) {
      return record.research_field.map(k => k?.keyword).filter(Boolean).join('\n');
    }
    return record.research_field || '';
  }, [record]);

  // 이메일, 전화번호, 연구실
  const contactInfo = useMemo(() => {
    if (!record) return { email: '', tel: '', lab: '' };
    return {
      email: record.email || '',
      tel: record.telephone || '',
      lab: record.lab_name || '',
    };
  }, [record]);

  // Research IDs 처리
  const ids = useMemo(() => {
    return Array.isArray(record?.research_ids) ? record.research_ids : [];
  }, [record]);

  const findId = useCallback((t) => {
    try {
      const it = ids.find(r => r?.id_type === t);
      return it?.id_value || '';
    } catch (e) {
      return '';
    }
  }, [ids]);

  const orcid = useMemo(() => record?.orcid || findId('orcid'), [record, findId]);
  const isni = useMemo(() => record?.isni || findId('isni'), [record, findId]);
  const scopusId = useMemo(() => record?.scopus_id || findId('scopus_id'), [record, findId]);
  const kciId = useMemo(() => record?.kci_id || findId('kci_id'), [record, findId]);
  const googleId = useMemo(() => record?.googlescholar_id || findId('googlescholar_id') || findId('google_scholar_id'), [record, findId]);
  const researchgateId = useMemo(() => record?.researchgate_id || findId('researchgate_id'), [record, findId]);
  const researcherId = useMemo(() => record?.researcher_id || findId('researcher_id'), [record, findId]);
  const wosId = useMemo(() => findId('wos_id') || findId('wos'), [findId]);

  // URLs 생성
  const orcidUrl = orcid ? `https://orcid.org/${orcid}` : '';
  const isniUrl = isni ? `https://nl.go.kr/isni/${isni}` : '';
  const scopusUrl = scopusId ? `https://www.scopus.com/authid/detail.uri?authorId=${encodeURIComponent(scopusId)}` : '';
  const googleUrl = googleId ? `https://scholar.google.com/citations?user=${encodeURIComponent(googleId)}` : '';
  const researchgateUrl = researchgateId ? `https://www.researchgate.net/profile/${encodeURIComponent(researchgateId)}` : '';
  const kciUrl = kciId ? `https://www.kci.go.kr/kciportal/po/citationindex/poCretDetail.kci?citationBean.cretId=${encodeURIComponent(kciId)}` : '';
  const researcherIdUrl = researcherId ? `https://www.researcherid.com/rid/${encodeURIComponent(researcherId)}` : '';
  const wosUrl = wosId ? `https://www.webofscience.com/wos/author/record/${encodeURIComponent(wosId)}` : '';


  const handleTabChange = (key) => {
    setActiveTab(key);
    // URL 파라미터 업데이트 (기본 intro는 파라미터 제거)
    if (key === 'intro') {
      navigate('/mypage/', { replace: true });
    } else {
      navigate(`/mypage/?tab=${key}`, { replace: true });
    }
  };

  const tabBtn = (key, label) => (
    <div className={`${activeTab === key ? 'active' : ''} sm:w-[50%] lg:hover:text-[#333]`}>
      <button className="h-[52px] px-6 cursor-pointer" onClick={() => handleTabChange(key)}>
        {label}
      </button>
    </div>
  );

  return (
    <div className="flexPage !gap-[40px] mt-[127px] lg:mt-[120px] lg:px-6 xl:px-[100px]">
      <div className="w-full lg:w-[calc((100%-40px)*0.7)] px-6 lg:px-0 mt-[30px] lg:mt-[68px]">
        <PageBreadcrumb current="My page" />

        <div className="tit-area w-full flex justify-between gap-3">
          <h2 className="text-[26px] leading-[130%] font-black lg:text-[32px]">My page</h2>
        </div>

        <div className="mt-[30px] lg:mt-8 lg:p-[60px] bg-white rounded-[12px]">
          <div className="relative xl:flex xl:items-start xl:gap-[28px] xl:flex-wrap">
            <div className="flex items-center justify-center shrink-0 w-[68px] h-[68px] mb-[10px] rounded-full overflow-hidden border border-[#E4E8F1] xl:w-[96px] xl:h-[96px]">
              <img src={record?.profile_pic || rmVars.themeUrl + 'images/no-profile-gy.png'} alt="photo" className="w-full h-full object-cover object-center" />
            </div>

            <div className="xl:w-[calc(100%-124px)]">
              <h2 className="mb-[7px] text-[18px] lg:text-[22px] leading-[130%] font-black xl:mb-[10px] 2xl:pr-[310px]">
                {loading ? '로딩 중...' : (fullName || '이름 없음')}
              </h2>

              <div className="flex flex-wrap gap-2 text-[14px] text-[#555] font-semibold">
                <span>{deptName || colName || ''}</span>
                {position && <span>/</span>}
                {position && <span>{position}</span>}
              </div>

              <div className="absolute top-[-67px] lg:top-[-28px] right-0 lg:right-[-28px] flex gap-2 items-center">
                <button
                  type="button"
                  onClick={handleEditClick}
                  className="btn-edit flex rounded-[12px] items-center justify-center gap-2 px-4 py-3 text-[14px] text-white font-semibold leading-[100%] bg-[#333] cursor-pointer"
                >
                  <img src={rmVars.themeUrl + 'images/ico-edit-wh.png'} className="w-[18px]" alt="edit" />
                  <span>Edit</span>
                </button>
              </div>

              <div className="flex flex-wrap gap-x-2 gap-y-1 mt-[15px]">
                {researcherIdUrl && (
                  <a href={researcherIdUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/wos-page.png'} className="w-[30px] lg:w-[20px]" alt="WoS ID" />
                    <span className="hidden lg:block">WoS ID</span>
                  </a>
                )}

                {googleUrl && (
                  <a href={googleUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/googlescholar-page.png'} className="w-[30px] lg:w-[20px]" alt="Google Scholar" />
                    <span className="hidden lg:block">Google Scholar</span>
                  </a>
                )}

                {orcidUrl && (
                  <a href={orcidUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/orcid-page.png'} className="w-[30px] lg:w-[20px]" alt="ORCID" />
                    <span className="hidden lg:block">ORCID</span>
                  </a>
                )}

                {isniUrl && (
                  <a href={isniUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/isni-page.png'} className="w-[30px] lg:w-[20px]" alt="ISNI" />
                    <span className="hidden lg:block">ISNI</span>
                  </a>
                )}

                {kciUrl && (
                  <a href={kciUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/kci-page.png'} className="w-[30px] lg:w-[20px]" alt="KCI" />
                    <span className="hidden lg:block">KCI</span>
                  </a>
                )}

                {researchgateUrl && (
                  <a href={researchgateUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/researchgate-page.png'} className="w-[30px] lg:w-[20px]" alt="ResearchGate" />
                    <span className="hidden lg:block">ResearchGate</span>
                  </a>
                )}

                {scopusUrl && (
                  <a href={scopusUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/scopus-page.png'} className="w-[30px] lg:w-[20px]" alt="Scopus" />
                    <span className="hidden lg:block">Scopus</span>
                  </a>
                )}

                {wosUrl && (
                  <a href={wosUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/wos-page.png'} className="w-[30px] lg:w-[20px]" alt="Web of Science" />
                    <span className="hidden lg:block">Web of Science</span>
                  </a>
                )}
              </div>

              {(contactInfo.email || contactInfo.tel || contactInfo.lab) && (
                <div className="flex flex-col gap-y-[14px] mt-6 pt-6 border-t border-t-[#E4E8F1] text-[14px] font-semibold leading-[100%] xl:border-b xl:border-b-[#E4E8F1] xl:flex-row xl:items-center xl:gap-x-3 xl:py-5 xl:mt-[18px]">
                  {contactInfo.email && (
                    <>
                      <div className="flex gap-x-[10px] xl:items-center">
                        <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                          <img src={rmVars.themeUrl + 'images/email-ico.png'} className="hidden xl:block w-[18px]" alt="email" />
                          <span className="xl:hidden">Email</span>
                        </div>
                        <a href={`mailto:${contactInfo.email}`} className="xl:text-[#555]">{contactInfo.email}</a>
                      </div>
                      {(contactInfo.tel || contactInfo.lab) && (
                        <span className="hidden xl:block w-[1px] h-[17px] bg-[#E6E8EC]"></span>
                      )}
                    </>
                  )}
                  {contactInfo.tel && (
                    <>
                      <div className="flex gap-x-[10px] xl:items-center">
                        <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                          <img src={rmVars.themeUrl + 'images/phone-ico.png'} className="hidden xl:block w-[18px]" alt="tel" />
                          <span className="xl:hidden">Tel</span>
                        </div>
                        <div className="xl:text-[#555]">
                          {contactInfo.tel && /^[\d-]{7,20}$/.test(contactInfo.tel.trim()) ? (
                            <a href={`tel:${contactInfo.tel.trim()}`} className="hover:underline">
                              {contactInfo.tel}
                            </a>
                          ) : (
                            contactInfo.tel
                          )}
                        </div>
                      </div>
                      {contactInfo.lab && (
                        <span className="hidden xl:block w-[1px] h-[17px] bg-[#E6E8EC]"></span>
                      )}
                    </>
                  )}
                  {contactInfo.lab && (
                    <div className="flex gap-x-[10px] xl:items-center">
                      <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                        <img src={rmVars.themeUrl + 'images/science-ico.png'} className="hidden xl:block w-[18px]" alt="lab" />
                        <span className="xl:hidden">Lab</span>
                      </div>
                      <div className="xl:text-[#555]">{contactInfo.lab}</div>
                    </div>
                  )}
                </div>
              )}

              {researchArea && (
                <div className="flex gap-x-[10px] mt-[14px] text-[14px] font-semibold leading-[100%] xl:mt-5 items-start">
                  <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto xl:flex items-center gap-1">
                    <img src={rmVars.themeUrl + 'images/researcharea-ico.png'} className="hidden xl:block w-[18px]" alt="Research Area" />
                    <span>Research Area</span>
                  </div>
                  <div className="xl:mt-[2px] whitespace-pre-line leading-[150%]">
                    {researchArea ? researchArea : '연구분야가 등록되어 있지 않습니다. 작성을 권유드립니다.'}
                  </div>
                </div>
              )}

              {/* 논문 확인 현황 요약 */}
              <div className="flex justify-between mt-5 py-4 px-5 text-center bg-[#F3F5F9] rounded-[12px]">
                <div className="flex flex-col gap-[6px] flex-1">
                  <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">전체 논문수</p>
                  <p className="text-[20px] font-black text-[#333]">{pubMeta.total ?? '--'}</p>
                </div>
                <div className="w-[1px] bg-[#E4E8F1] shrink-0" />
                <div className="flex flex-col gap-[6px] flex-1">
                  <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">저자가 확인한 논문수</p>
                  <p className="text-[20px] font-black text-[#4B4DFF]">{pubMeta.confirmed ?? '--'}</p>
                </div>
                <div className="w-[1px] bg-[#E4E8F1] shrink-0" />
                <div className="flex flex-col gap-[6px] flex-1">
                  <p className="text-[13px] font-bold text-[#6C7687] leading-[120%]">미확인 논문수</p>
                  <p className="text-[20px] font-black text-[#F3652D]">{pubMeta.unconfirmed ?? '--'}</p>
                </div>
              </div>

              <div className="flex gap-x-[10px] mt-[14px] text-[14px] font-semibold leading-[100%] lg:hidden">
                <div className="shrink-0 w-[76px] mt-[10px] text-[#8D9AAF] font-medium">H-Index</div>
                <div className="flex flex-wrap gap-[6px]">
                  {['Web of Science', 'Scopus', 'KCI'].map((label, idx) => (
                    <div key={idx} className="px-[10px] py-[7px] text-[13px] font-medium text-[#532EB3] border border-[#E4E8F1] rounded-[12px]">
                      {label} <span className="ml-1 py-[2px] px-[6px] bg-[#532EB3] text-white font-bold rounded-full">--</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-x-[10px] items-center mt-[14px] text-[14px] font-semibold leading-[100%] lg:hidden">
                <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium">Schoarly Output</div>
                <div>{scholarlyOutput}</div>
              </div>

              <div className="flex gap-x-[10px] items-center mt-[14px] text-[14px] font-semibold leading-[100%] lg:hidden">
                <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium">Citation Count</div>
                <div>{citationCountFormatted}</div>
              </div>
            </div>
          </div>

          <div className="chartarea p-6 mt-5 border border-[#E4E8F1] rounded-[12px] lg:hidden"><DocCiteTrendsMini data={pubYearData} /></div>

          <div id="introTooltip" className="fixed hidden w-[140px] p-4 text-white text-[12px] leading-[100%] bg-[#4B4DFF] rounded-[12px] sm:w-[180px] md:w-[200px] lg:w-[234px]">
            <p className="mb-[6px] pr-4 font-semibold">아직 소개글이 입력되지 않았습니다.</p>
            <a href="#" className="font-medium underline">내 정보 수정하러 가기</a>
            <button id="tooltipClose" className="absolute top-4 right-4 cursor-pointer">
              <img src={rmVars.themeUrl + 'images/clear-icon-fff.png'} className="w-[10px]" alt="close" />
            </button>
            <div className="absolute bottom-[-10px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-[#4B4DFF]"></div>
          </div>

          {/* 탭 메뉴 */}
          <div className="researcher-cont-tabM tabM mt-[30px] mx-[-24px] text-[15px] font-medium text-[#8D9AAF] text-center border-b border-b-[#E4E8F1] overflow-x-auto sm:overflow-x-hidden lg:mx-[-60px] lg:mt-[52px]">
            <div className="tab-wrapper w-max sm:w-full">
              {tabBtn('intro', 'Introduction')}
              {tabBtn('metrics', 'Researcher Metrics')}
              {tabBtn('co', 'Co-Researchers')}
              {tabBtn('pub', 'Publications')}
            </div>
          </div>

          {/* 탭 콘텐츠 */}
          <div className="researcher-tabM-cont tabM-cont">
            {activeTab === 'intro' && <IntroTab record={record} loading={loading} error={error} />}
            {activeTab === 'metrics' && <ScholarMetricsTab record={record} />}
            {activeTab === 'co' && <CoResearchersTab />}
            {activeTab === 'pub' && <PublicationsTab />}
          </div>
        </div>
      </div>
      {/* right box */}
      <div className="right-box hidden relative mt-[181px] w-[calc((100%-40px)*0.3)] shrink-0 lg:block">
        <div className="w-full p-10 bg-white rounded-xl">
          <div className="mb-10">
            <h2 className="flex items-center gap-[6px] mb-[14px]">
              <span className="text-[18px] font-bold leading-[100%]">H-Index</span>
            </h2>

            <div className="flex flex-wrap gap-2">
              <div className="px-4 py-[10px] text-[13px] font-medium text-[#532EB3] leading-[100%] border border-[#E4E8F1] rounded-[12px]">
                Web of Science <span className="ml-1 py-[2px] px-[6px] bg-[#532EB3] text-white font-bold rounded-full">{hIndexWos}</span>
              </div>
              <div className="px-4 py-[10px] text-[13px] font-medium text-[#F3652D] leading-[100%] border border-[#E4E8F1] rounded-[12px]">
                Scopus <span className="ml-1 py-[2px] px-[6px] bg-[#F3652D] text-white font-bold rounded-full">{hIndexScopus}</span>
              </div>
              <div className="px-4 py-[10px] text-[13px] font-medium text-[#2D7ECF] leading-[100%] border border-[#E4E8F1] rounded-[12px]">
                KCI <span className="ml-1 py-[2px] px-[6px] bg-[#2D7ECF] text-white font-bold rounded-full">{hIndexKci}</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="flex items-center gap-[6px] mb-[14px]">
              <span className="text-[18px] font-bold leading-[100%]">
                Document Citation trends
              </span>
            </h2>

            <div className="flex flex-col flex-wrap gap-3 2xl:flex-row">
              <div className="w-full px-6 py-5 font-bold leading-[100%] border border-[#E4E8F1] rounded-[12px] 2xl:w-[calc((100%-12px)/2)]">
                <h3 className="mb-[6px] text-[14px] text-[#8D9AAF]">Schoarly Output</h3>
                <span className="text-[18px] text-[#376BDB]">{scholarlyOutput}</span>
              </div>
              <div className="w-full px-6 py-5 font-bold leading-[100%] border border-[#E4E8F1] rounded-[12px] 2xl:w-[calc((100%-12px)/2)]">
                <h3 className="mb-[6px] text-[14px] text-[#8D9AAF]">Citation Count</h3>
                <span className="text-[18px] text-[#376BDB]">{citationCountFormatted}</span>
              </div>
              <div className="w-full px-5 py-8 border border-[#E4E8F1] rounded-[12px]">
                <DocCiteTrendsMini data={pubYearData} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
